import type { LocalFile, SecureNote } from '@frontend/api-types';
import { type ReplaceSecureNoteInput } from '@frontend/api-types';
import { match, type SnakeCasedProperties } from '@frontend/duck-tape';
import type { SecureNoteFormSchema } from '@frontend/forms';
import {
  checkDidSecureNoteFormValuesChange,
  findRemovedAttachments,
  getSecureNoteFormDefaultValues,
  makeSavedFileList,
  useSecureNoteForm,
} from '@frontend/forms';
import { Fragment, useEffect, useState, useVisibilityController } from '@frontend/react';
import {
  Button,
  ConfirmationModal,
  LinkButton,
  Modal,
  Spinner,
  Text,
  Title,
  XStack,
  YStack,
  type CommonModalProps,
} from '@frontend/web-react';
import { WebTypedFormData } from '@frontend/web-utils';
import {
  useAnalytics,
  useDeleteSecureNoteMutation,
  useLazyListCommentsQuery,
  useLazyListSecureNotesQuery,
  useLazyRetrieveSecureNoteContentQuery,
  useReplaceSecureNoteContentMutation,
  useRetrieveClientQuery,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { Modal as MantineModal } from '@mantine/core';
import { SecureSessionLiveView } from '../SecureSessions/SecureSessionLiveView';
import { SecureNoteForm } from './SecureNoteForm';
import { SecureNoteTypeSelection } from './SecureNoteTypeSelection';

const isCompleteSecureNote = (secureNote: SecureNote | undefined): secureNote is SecureNote => {
  return (
    secureNote !== undefined &&
    'isBbContextReadyForService' in secureNote &&
    'webDomain' in secureNote &&
    typeof secureNote.isBbContextReadyForService === 'boolean' &&
    (typeof secureNote.webDomain === 'string' || secureNote.webDomain === null)
  );
};

export type SecureNoteModalProps = CommonModalProps & {
  secureNote?: SecureNote;
  secureNoteId: MaybeUndefined<string>;
  taskId?: string;
  title?: string;
};

export const UpdateSecureNoteModal = ({
  isOpen,
  onClose,
  secureNote,
  secureNoteId,
  taskId,
  title,
}: SecureNoteModalProps) => {
  const [selectedSecureNoteType, setSelectedSecureNoteType] = useState<
    'secure-note' | 'secure-session' | 'select-type' | 'session-active'
  >('secure-note');
  const [isEditing, setIsEditing] = useState(false);

  const { data: client } = useRetrieveClientQuery();
  const [listSecureNotes, { data: secureNotes, isLoading: isLoadingSecureNotes }] = useLazyListSecureNotesQuery();
  const [retrieveSecureNoteContent, { data: secureNoteContent, isLoading: isRetrieveSecureNoteContentLoading }] =
    useLazyRetrieveSecureNoteContentQuery();
  const [replaceSecureNoteContent, { isLoading: isReplaceSecureNoteContentLoading }] =
    useReplaceSecureNoteContentMutation();
  const [deleteSecureNote, { isLoading: isDeleteSecureNoteLoading }] = useDeleteSecureNoteMutation();
  const [retrieveComments] = useLazyListCommentsQuery();
  const [isDeleteSecureNoteModalOpen, { close: closeDeleteSecureNoteModal, open: openDeleteSecureNoteModal }] =
    useVisibilityController();
  const { track } = useAnalytics();

  const [completeSecureNote, setCompleteSecureNote] = useState<SecureNote | undefined>(undefined);

  useEffect(() => {
    if (!isOpen || !secureNoteId || !client?.id) {
      setCompleteSecureNote(undefined);
      return;
    }

    if (isCompleteSecureNote(secureNote)) {
      setCompleteSecureNote(secureNote);
      return;
    }

    if (secureNotes) {
      const foundSecureNote = secureNotes.find((sn) => sn.id === secureNoteId);
      if (foundSecureNote) {
        if (
          'isBbContextReadyForService' in foundSecureNote &&
          'webDomain' in foundSecureNote &&
          typeof foundSecureNote.isBbContextReadyForService === 'boolean'
        ) {
          setCompleteSecureNote(foundSecureNote as SecureNote);
          return;
        }
      }
    }

    setCompleteSecureNote(undefined);
    listSecureNotes({ clientId: client.id });
  }, [isOpen, secureNoteId, client?.id, secureNote, listSecureNotes, secureNotes]);

  const activeSecureNote = completeSecureNote ?? secureNote;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (secureNoteId && isRetrieveSecureNoteContentLoading) {
      return;
    }

    if (!isCompleteSecureNote(activeSecureNote)) {
      setSelectedSecureNoteType('secure-note');
      return;
    }

    if (activeSecureNote.isBbContextReadyForService) {
      setSelectedSecureNoteType('session-active');
    } else if (activeSecureNote.webDomain) {
      setSelectedSecureNoteType('select-type');
    } else {
      setSelectedSecureNoteType('secure-note');
    }
  }, [
    isOpen,
    secureNoteId,
    isRetrieveSecureNoteContentLoading,
    activeSecureNote?.isBbContextReadyForService,
    activeSecureNote?.webDomain,
  ]);

  useEffect(() => {
    if (secureNoteId) {
      track({ data: { secureNoteId }, event: 'secureNoteViewed', topic: 'secureNote' });
    }
  }, [secureNoteId, track]);

  useEffect(() => {
    if (isOpen && secureNoteId) {
      retrieveSecureNoteContent(secureNoteId);
    }
  }, [isOpen, secureNoteId, retrieveSecureNoteContent]);

  const onUpdateSecureNoteContent = async (values: SecureNoteFormSchema) => {
    if (!checkDidSecureNoteFormValuesChange(secureNoteContent!, values)) {
      setIsEditing(false);
      onClose();
      return;
    }
    if (!secureNoteId) return;
    track({ data: { secureNoteId }, event: 'secureNoteUpdated', topic: 'secureNote' });
    const { attachments, notes, password, username, website } = values;
    return handleMutation({
      mutation: async () => {
        const formData = new WebTypedFormData<SnakeCasedProperties<ReplaceSecureNoteInput>>();
        const newAttachments = attachments.filter((a) => a.__typename === 'LocalFile') as LocalFile[];
        const deleteAttachmentsKeys: string[] = findRemovedAttachments(
          makeSavedFileList(secureNoteContent?.attachments ?? [], (a) => a),
          attachments,
        ).map((a) => a.key);

        if (newAttachments.length) formData.appendLocalFiles('attachments', newAttachments);
        if (deleteAttachmentsKeys.length) formData.append('delete_attachments', deleteAttachmentsKeys);
        if (notes) formData.append('notes', notes);
        if (password) formData.append('password', password);
        if (username) formData.append('username', username);
        if (website) formData.append('website', website);
        if (taskId) formData.append('task', taskId);
        return replaceSecureNoteContent({ formData, secureNoteId });
      },
      onSuccess: () => {
        retrieveSecureNoteContent(secureNoteId);
        if (taskId) retrieveComments({ format: 'markdown', id: taskId });
        setIsEditing(false);
        onClose();
      },
      showErrorToast: true,
      showSuccessToast: true,
      successMessage: isEditing ? 'Successfully edited.' : 'Successfully added.',
    });
  };

  const onDeleteSecureNote = async () => {
    if (!secureNoteId) return;
    track({ data: { secureNoteId }, event: 'secureNoteDeleted', topic: 'secureNote' });
    return handleMutation({
      mutation: async () => deleteSecureNote({ secureNoteId, taskId: taskId! }),
      onSuccess: () => {
        closeDeleteSecureNoteModal();
        onClose();
        if (taskId) retrieveComments({ format: 'markdown', id: taskId });
      },
      showErrorToast: true,
      showSuccessToast: true,
      successMessage: 'Successfully deleted.',
    });
  };

  const isNew =
    !secureNoteContent?.attachments?.length &&
    !secureNoteContent?.notes &&
    !secureNoteContent?.password &&
    !secureNoteContent?.username &&
    !secureNoteContent?.website;

  const hideSubmissionFooter = !isNew && !isEditing;
  const disableInputs = isNew ? false : !isEditing;
  const isLoadingCompleteSecureNote = isOpen && secureNoteId && !completeSecureNote && isLoadingSecureNotes;
  const hasSecureSessionInfo = completeSecureNote !== undefined || isCompleteSecureNote(secureNote);
  const isLoadingSecureSessionInfo = isOpen && secureNoteId && !hasSecureSessionInfo && isLoadingSecureNotes;
  const shouldShowActionButtons =
    !isNew &&
    !isEditing &&
    selectedSecureNoteType !== 'secure-session' &&
    selectedSecureNoteType !== 'select-type' &&
    selectedSecureNoteType !== 'session-active';

  const { getControl, isValid, setFieldValue, submitForm, values } = useSecureNoteForm({
    enableReinitialize: true,
    initialValues: getSecureNoteFormDefaultValues({ ...secureNoteContent, title: title ?? '' }),
    onSubmit: onUpdateSecureNoteContent,
  });

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  const enableEditMode = () => {
    setSelectedSecureNoteType('secure-note');
    setIsEditing(true);
  };

  return (
    <Fragment>
      <Modal
        Header={
          <MantineModal.Header className="flex flex-col min-h-none items-start">
            <XStack className="flex flex-row justify-between flex-1 w-full">
              <Title textAlign="start" type="h4">
                {title}
              </Title>
              {shouldShowActionButtons ? (
                <XStack gapX="md">
                  <LinkButton
                    accessibilityLabel="Edit secure note"
                    label="Edit"
                    leftIconName="IconPencil"
                    onClick={enableEditMode}
                  />
                  <LinkButton
                    accessibilityLabel="Delete secure note"
                    color="danger"
                    iconColor="danger"
                    label="Delete"
                    leftIconName="IconTrash"
                    onClick={openDeleteSecureNoteModal}
                  />
                </XStack>
              ) : null}
            </XStack>
          </MantineModal.Header>
        }
        headerClassName="flex-start"
        headerGap="none"
        isOpen={isOpen}
        onClose={handleClose}
        size="500px"
        titlesAlignment="start"
      >
        {isLoadingCompleteSecureNote || isLoadingSecureSessionInfo ? (
          <YStack
            alignItems="center"
            className="w-full"
            gap="md"
            justifyContent="center"
            style={{ minHeight: '320px' }}
          >
            <Spinner />
            <Text className="text-center" color="tertiary" type="p-md">
              {isLoadingCompleteSecureNote
                ? 'Loading secure note details...'
                : 'Checking secure session compatibility...'}
            </Text>
          </YStack>
        ) : (
          match(selectedSecureNoteType)
            .with('session-active', () => (
              <YStack className="w-full" gap="lg">
                <YStack gap="md">
                  <Text type="p-md">
                    Your secure session is active. Your info is encrypted and only accessible to our team.
                  </Text>
                  <Text color="tertiary" type="p-sm">
                    {'You can also add or update your login details to help our team access your accounts.'}
                  </Text>
                </YStack>
                <YStack gap="sm">
                  <Button
                    accessibilityLabel="Update secure note credentials"
                    label="Update Credentials"
                    leftIconName="IconPencil"
                    onClick={enableEditMode}
                    variant="secondary"
                  />
                  <Button
                    accessibilityLabel="Close secure session"
                    label="Close"
                    onClick={handleClose}
                    variant="primary"
                  />
                </YStack>
              </YStack>
            ))
            .with('select-type', () => (
              <SecureNoteTypeSelection
                onSelectManual={() => setSelectedSecureNoteType('secure-note')}
                onSelectSecureSession={() => setSelectedSecureNoteType('secure-session')}
                title={title ?? ''}
              />
            ))
            .with('secure-session', () =>
              secureNoteId ? (
                <SecureSessionLiveView
                  onClose={() => {
                    setSelectedSecureNoteType('select-type');
                    handleClose();
                  }}
                  secureNoteId={secureNoteId}
                  taskId={taskId}
                />
              ) : null,
            )
            .with('secure-note', () => (
              <SecureNoteForm
                disableInputs={disableInputs}
                getControl={getControl}
                hideSubmissionFooter={hideSubmissionFooter}
                isNew={isNew}
                isReplaceSecureNoteContentLoading={isReplaceSecureNoteContentLoading}
                isValid={isValid}
                setFieldValue={setFieldValue}
                submitForm={submitForm}
                values={values}
              />
            ))
            .exhaustive()
        )}
      </Modal>
      <ConfirmationModal
        isOpen={isDeleteSecureNoteModalOpen}
        isSubmitLoading={isDeleteSecureNoteLoading}
        onClickCancel={closeDeleteSecureNoteModal}
        onClickSubmit={onDeleteSecureNote}
        onClose={closeDeleteSecureNoteModal}
        submitLabel="Delete"
        subtitle="Our copilots will have to ask you this detail in the future."
        title="Are you sure you want to delete this secure info?"
        variant="destructive"
      />
    </Fragment>
  );
};
